import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import Loader from "../components/Loader";
import * as pdfjsLib from "pdfjs-dist";
import { PDFDocument } from "pdf-lib";


pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const BASE_URL = "https://service.hashcashconsultants.com/billbitcoins-v2/merchant";

const resolveApiUrls = (processor, token, uuid) => {
  if (processor === "epx") {
    return {
      fetchUrl: `${BASE_URL}/getepx-document/${token}`,
      uploadUrl: `${BASE_URL}/epxsigned-document/${token}/${uuid}`,
    };
  }
  // Default pattern used by tsys and future processors
  return {
    fetchUrl: `${BASE_URL}/${processor}-document/${token}`,
    uploadUrl: `${BASE_URL}/${processor}/signed-document/${token}`,
  };
};

const SignedDocs = () => {
  const { processor, token, uuid } = useParams();

  const { fetchUrl: FETCH_DOC_URL, uploadUrl: UPLOAD_DOC_URL } = resolveApiUrls(processor, token, uuid);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Each entry: { dataUrl, viewportWidth, viewportHeight }
  const [pages, setPages] = useState([]);
  const [pdfBytes, setPdfBytes] = useState(null);
  const [hasSigned, setHasSigned] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // One canvas ref per page
  const sigCanvasRefs = useRef([]);
  const isDrawingRef = useRef(false);
  const activeIdxRef = useRef(null);
  const lastPosRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (token) fetchDocument();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchDocument = async () => {
    setLoading(true);
    setError(null);
    setPages([]);
    setPdfBytes(null);
    setHasSigned(false);
    setUploadSuccess(false);
    setUploadError(null);
    sigCanvasRefs.current = [];
    try {
      const response = await fetch(
        FETCH_DOC_URL,
        { method: "GET" }
      );
      if (!response.ok) throw new Error("Request failed");

      const arrayBuffer = await response.arrayBuffer();

      // Slice creates an independent copy for pdf-lib.
      // PDF.js transfers the underlying ArrayBuffer to its Web Worker,
      // which detaches it and makes the original unusable for pdf-lib later.
      setPdfBytes(new Uint8Array(arrayBuffer.slice()));

      // Render each page to a data URL using PDF.js
      const pdfDoc = await pdfjsLib.getDocument({ data: new Uint8Array(arrayBuffer) }).promise;
      const rendered = [];
      for (let i = 1; i <= pdfDoc.numPages; i++) {
        const page = await pdfDoc.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        const ctx = canvas.getContext("2d");
        await page.render({ canvasContext: ctx, viewport }).promise;
        rendered.push({
          dataUrl: canvas.toDataURL(),
          viewportWidth: viewport.width,
          viewportHeight: viewport.height,
        });
      }
      setPages(rendered);
      setLoading(false);
    } catch {
      setLoading(false);
      setError("Failed to load the document. The link may be invalid or expired.");
    }
  };

  // ── Upload signed document ─────────────────────────────────────────────────

  const handleUpload = async () => {
    if (!hasSigned || !pdfBytes) return;
    setUploading(true);
    setUploadError(null);
    try {
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pdfPages = pdfDoc.getPages();

      // Embed each page's canvas drawing into the corresponding PDF page
      for (let i = 0; i < sigCanvasRefs.current.length; i++) {
        const canvas = sigCanvasRefs.current[i];
        if (!canvas) continue;

        // Skip pages that have no drawing (all pixels fully transparent)
        const imgData = canvas.getContext("2d").getImageData(0, 0, canvas.width, canvas.height);
        const hasDrawing = imgData.data.some((val, j) => j % 4 === 3 && val > 0);
        if (!hasDrawing) continue;

        const sigRes = await fetch(canvas.toDataURL("image/png"));
        const sigBytes = new Uint8Array(await sigRes.arrayBuffer());

        const pngImage = await pdfDoc.embedPng(sigBytes);
        const { width, height } = pdfPages[i].getSize();
        pdfPages[i].drawImage(pngImage, { x: 0, y: 0, width, height });
      }

      const signedBytes = await pdfDoc.save();

      // POST signed PDF as raw binary
      const uploadRes = await fetch(
        UPLOAD_DOC_URL,
        {
          method: "POST",
          headers: { "Content-Type": "application/pdf" },
          body: signedBytes,
        }
      );
      const result = await uploadRes.json();
      if (!uploadRes.ok || !result?.status || !result?.data?.status) {
        const uploadResponse = result?.data?.uploadResponse;
        const errorMsg =
          (uploadResponse?.status === "error" && uploadResponse?.data?.errors) ||
          result?.data?.message ||
          "Upload failed";
        throw new Error(errorMsg);
      }
      setUploading(false);
      setUploadSuccess(true);
    } catch (err) {
      setUploading(false);
      setUploadError(err?.message || "Upload failed. Please try again.");
    }
  };

  // ── Signature pad helpers ──────────────────────────────────────────────────

  const getCanvasPos = (e, idx) => {
    const canvas = sigCanvasRefs.current[idx];
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY,
    };
  };

  const startDrawing = (e, idx) => {
    e.preventDefault();
    isDrawingRef.current = true;
    activeIdxRef.current = idx;
    lastPosRef.current = getCanvasPos(e, idx);
  };

  const draw = (e, idx) => {
    e.preventDefault();
    if (!isDrawingRef.current || activeIdxRef.current !== idx) return;
    const canvas = sigCanvasRefs.current[idx];
    const ctx = canvas.getContext("2d");
    const pos = getCanvasPos(e, idx);

    ctx.strokeStyle = "#1a1a2e";
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";

    ctx.beginPath();
    ctx.moveTo(lastPosRef.current.x, lastPosRef.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();

    lastPosRef.current = pos;
    if (!hasSigned) setHasSigned(true);
  };

  const stopDrawing = () => {
    isDrawingRef.current = false;
    activeIdxRef.current = null;
  };

  const clearSignature = () => {
    sigCanvasRefs.current.forEach((canvas) => {
      if (canvas) {
        canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
      }
    });
    setHasSigned(false);
    setUploadSuccess(false);
    setUploadError(null);
  };

  // ── Styles ─────────────────────────────────────────────────────────────────

  const styles = {
    page: {
      minHeight: "100vh",
      background: "var(--body-color)",
      display: "flex",
      flexDirection: "column",
    },
    content: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      padding: "24px",
      gap: "24px",
      maxWidth: "960px",
      width: "100%",
      margin: "0 auto",
      boxSizing: "border-box",
    },
    errorCard: {
      background: "var(--popupbody-color)",
      borderRadius: "12px",
      padding: "48px 32px",
      textAlign: "center",
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    },
    errorIcon: {
      width: "64px",
      height: "64px",
      borderRadius: "50%",
      background: "rgba(231, 76, 60, 0.1)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      margin: "0 auto 16px",
    },
    errorTitle: {
      fontSize: "18px",
      fontWeight: "600",
      color: "var(--text-color)",
      marginBottom: "8px",
    },
    errorMsg: {
      fontSize: "14px",
      color: "var(--text-color)",
      opacity: 0.65,
      marginBottom: "24px",
    },
    retryBtn: {
      padding: "10px 24px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#ffffff",
      background: "var(--btn-color)",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
    },
    pdfCard: {
      background: "var(--popupbody-color)",
      borderRadius: "12px",
      overflow: "hidden",
      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
    },
    pdfLabel: {
      padding: "14px 20px",
      borderBottom: "1px solid var(--body-color-less2)",
      fontSize: "13px",
      fontWeight: "600",
      color: "var(--text-color)",
      display: "flex",
      alignItems: "center",
      gap: "8px",
    },
    pdfPages: {
      padding: "16px",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      background: "#e8e8e8",
      maxHeight: "70vh",
      overflowY: "auto",
    },
    pageWrapper: {
      position: "relative",
      boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
      lineHeight: 0,
    },
    pageImg: {
      width: "100%",
      display: "block",
    },
    sigCanvas: {
      position: "absolute",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      display: "block",
      touchAction: "none",
    },
    footer: {
      padding: "16px 20px",
      borderTop: "1px solid var(--body-color-less2)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: "12px",
      flexWrap: "wrap",
    },
    footerLeft: {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      flexWrap: "wrap",
    },
    clearBtn: {
      fontSize: "12px",
      fontWeight: "500",
      color: "var(--text-color)",
      background: "var(--body-color)",
      border: "1px solid var(--body-color-less2)",
      borderRadius: "6px",
      padding: "5px 12px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "5px",
    },
    uploadStatus: {
      fontSize: "13px",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },
    uploadBtn: {
      padding: "11px 28px",
      fontSize: "14px",
      fontWeight: "600",
      color: "#ffffff",
      background: "var(--btn-color)",
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      transition: "opacity 0.2s",
      flexShrink: 0,
    },
    uploadBtnDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  };

  const isUploadDisabled = !hasSigned || uploading || uploadSuccess;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <Helmet>
        <title>Sign Document</title>
      </Helmet>

      {(loading || uploading) && <Loader />}

      <div style={styles.page}>
        <div style={styles.content}>
          {!loading && error && (
            <div style={styles.errorCard}>
              <div style={styles.errorIcon}>
                <i
                  className="fa fa-triangle-exclamation"
                  style={{ color: "#e74c3c", fontSize: "28px" }}
                ></i>
              </div>
              <p style={styles.errorTitle}>Unable to Load Document</p>
              <p style={styles.errorMsg}>{error}</p>
              <button style={styles.retryBtn} onClick={fetchDocument}>
                <i className="fa fa-rotate-right" style={{ marginRight: "6px" }}></i>
                Try Again
              </button>
            </div>
          )}

          {!loading && pages.length > 0 && (
            <div style={styles.pdfCard}>
              <div style={styles.pdfLabel}>
                <i className="fa fa-file-pdf" style={{ color: "#e74c3c" }}></i>
                Sign Document
              </div>

              <div style={styles.pdfPages}>
                {pages.map((pg, idx) => (
                  <div key={idx} style={styles.pageWrapper}>
                    <img
                      src={pg.dataUrl}
                      alt={`Page ${idx + 1}`}
                      style={styles.pageImg}
                    />
                    <canvas
                      ref={(el) => (sigCanvasRefs.current[idx] = el)}
                      width={pg.viewportWidth}
                      height={pg.viewportHeight}
                      style={{
                        ...styles.sigCanvas,
                        cursor: uploadSuccess ? "default" : "crosshair",
                        opacity: uploadSuccess ? 0.5 : 1,
                      }}
                      onMouseDown={uploadSuccess ? undefined : (e) => startDrawing(e, idx)}
                      onMouseMove={uploadSuccess ? undefined : (e) => draw(e, idx)}
                      onMouseUp={uploadSuccess ? undefined : stopDrawing}
                      onMouseLeave={uploadSuccess ? undefined : stopDrawing}
                      onTouchStart={uploadSuccess ? undefined : (e) => startDrawing(e, idx)}
                      onTouchMove={uploadSuccess ? undefined : (e) => draw(e, idx)}
                      onTouchEnd={uploadSuccess ? undefined : stopDrawing}
                    />
                  </div>
                ))}
              </div>

              <div style={styles.footer}>
                <div style={styles.footerLeft}>
                  <button
                    style={styles.clearBtn}
                    onClick={clearSignature}
                    disabled={uploading}
                  >
                    <i className="fa fa-eraser"></i>
                    Clear Signature
                  </button>
                  {uploadSuccess && (
                    <span style={{ ...styles.uploadStatus, color: "#27ae60" }}>
                      <i className="fa fa-circle-check"></i>
                      Document uploaded successfully
                    </span>
                  )}
                  {uploadError && (
                    <span style={{ ...styles.uploadStatus, color: "#e74c3c" }}>
                      <i className="fa fa-circle-exclamation"></i>
                      {uploadError}
                    </span>
                  )}
                </div>

                <button
                  style={{
                    ...styles.uploadBtn,
                    ...(isUploadDisabled ? styles.uploadBtnDisabled : {}),
                  }}
                  disabled={isUploadDisabled}
                  onClick={handleUpload}
                >
                  <i className="fa fa-upload"></i>
                  {uploading ? "Uploading..." : "Submit Signed Document"}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SignedDocs;
